/**
 * To debug re-renders with why-did-you-render library, uncomment this import
 * and disable Prettier vscode extension (this import should be first of all)
 */
// import 'lib/whyDidYouRender';
import Head from 'next/head';
import { useRouter } from 'next/router';
import Script from 'next/script';
import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';

import GlobalLayout from 'components/Layout/Global';
import GlobalScripts from 'components/Scripts/GlobalScripts';

import { parseToBool } from 'lib/string';

import GlobalProvider from 'providers/GlobalProvider';

import APP from 'constants/app';

import 'style/globals.css';

// Stores the href of the offer card nearest to the viewport center
// when navigating away. Used to refine scroll position after back navigation
// since pixel-based restoration is inaccurate due to lazy-loaded images
// changing the document height.
let savedAnchorHref = null;

const PromobitApp = (props) => {
  const { Component, pageProps } = props || {};
  const router = useRouter();
  const correctionTimerRef = useRef(null);

  /**
   * TEMPORARY — infra canary for the EKS migration. Remove together with
   * `sendSupixCanary` once the cutover is done.
   *
   * Opt-in via `?supix_canary=1`, so organic traffic never triggers it. Read
   * from `window.location` rather than the router to avoid depending on when
   * query params hydrate. Imported dynamically so it costs nothing on the
   * overwhelming majority of loads, where the param is absent.
   */
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const params = new URLSearchParams(window.location.search);

    if (params.get('supix_canary') !== '1') {
      return;
    }

    import('lib/supix').then(({ sendSupixCanary }) => sendSupixCanary());
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const onRouteChangeStart = () => {
      // Save the offer card closest to viewport center
      const viewportMiddle = window.scrollY + window.innerHeight / 2;
      const cards = document.querySelectorAll('a[href*="/oferta/"]');
      let closest = null;
      let closestDist = Infinity;

      cards.forEach((el) => {
        const rect = el.getBoundingClientRect();
        const elMiddle = window.scrollY + rect.top + rect.height / 2;
        const dist = Math.abs(elMiddle - viewportMiddle);
        if (dist < closestDist) {
          closestDist = dist;
          closest = el;
        }
      });

      savedAnchorHref = closest ? closest.getAttribute('href') : null;
    };

    const onPopState = () => {
      if (!savedAnchorHref) {
        return;
      }

      const href = savedAnchorHref;

      // Wait for Next.js scroll restoration to happen first, then correct
      let attempts = 0;
      const correct = () => {
        const el = document.querySelector(`a[href="${href}"]`);
        if (el) {
          el.scrollIntoView({ block: 'center', behavior: 'instant' });
          savedAnchorHref = null;
          correctionTimerRef.current = null;
        } else if (attempts < 50) {
          attempts += 1;
          correctionTimerRef.current = setTimeout(correct, 100);
        } else {
          savedAnchorHref = null;
          correctionTimerRef.current = null;
        }
      };

      // Delay to let Next.js do its pixel-based scroll first (which triggers
      // infinite scroll loading), then correct to the exact element
      correctionTimerRef.current = setTimeout(correct, 500);
    };

    router.events.on('routeChangeStart', onRouteChangeStart);
    window.addEventListener('popstate', onPopState);

    return () => {
      router.events.off('routeChangeStart', onRouteChangeStart);
      window.removeEventListener('popstate', onPopState);
      if (correctionTimerRef.current) {
        clearTimeout(correctionTimerRef.current);
      }
    };
  }, [router]);

  if (!Component) {
    return null;
  }

  return (
    <GlobalProvider {...(pageProps?.[APP.CTX_OPTIONAL_PROPS] || {})}>
      {/* AdSense + Funding Choices (cascata) somam ~3.8s de TBT mobile no
       * profiling (PRD 3 / Lote 1). lazyOnload posterga o carregamento
       * pra após o `load` event, removendo o blocking do critical path
       * sem afetar a renderização dos slots — `(window.adsbygoogle = ...).push({})`
       * em AdSenseSlot bufferiza chamadas até a lib carregar. */}
      <Script
        crossOrigin="anonymous"
        id="adsbygoogle-script"
        src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-9700650747779397"
        strategy="lazyOnload"
      />
      {/* AppsFlyer SDK precisa morar fora de _document.js — strategies que não
       * sejam beforeInteractive dependem do useEffect client-side do <Script>,
       * que não dispara em _document (server-only, não hidratado). */}
      {parseToBool(process.env.NEXT_PUBLIC_APPS_FLYER_ENABLED) && (
        <Script id="appsflyer-script" strategy="lazyOnload">
          {`!function(t,e,n,s,a,c,i,o,p){t.AppsFlyerSdkObject=a,t.AF=t.AF||function(){
        (t.AF.q=t.AF.q||[]).push([Date.now()].concat(Array.prototype.slice.call(arguments)))},
        t.AF.id=t.AF.id||i,t.AF.plugins={},o=e.createElement(n),p=e.getElementsByTagName(n)[0],o.async=1,
        o.src="https://websdk.appsflyer.com?"+(c.length>0?"st="+c.split(",").sort().join(",")+"&":"")+(i.length>0?"af_id="+i:""),
        p.parentNode.insertBefore(o,p)}(window,document,"script",0,"AF","banners",{banners: {key: "${process.env.NEXT_PUBLIC_APPS_FLYER_WEB_KEY}" }});
        // Smart Banners are by default set to the max z-index value, so they won't be hidden by the website elements. This can be changed if you want some website components to be on top of the banner.
        AF('banners', 'showBanner');`}
        </Script>
      )}
      <GlobalScripts />
      <GlobalLayout
        isRecoveryPasswordValid={
          !!pageProps?.recoveryPassword && pageProps.recoveryPassword?.isValid
        }
      >
        <Head>
          {/*
            Customize `viewport` meta-tag to avoid auto zoom in input when using
            iPhone devices
          */}
          <meta
            content="minimum-scale=1, width=device-width, initial-scale=1.0, maximum-scale=1.0, viewport-fit=cover, shrink-to-fit=no"
            name="viewport"
          />
        </Head>
        <Component {...pageProps} />
      </GlobalLayout>
    </GlobalProvider>
  );
};

PromobitApp.propTypes = {
  pageProps: PropTypes.shape({
    // eslint-disable-next-line react/forbid-prop-types
    categories: PropTypes.array,
    // eslint-disable-next-line react/forbid-prop-types
    stores: PropTypes.array,
    topbarAd: PropTypes.shape({
      imageSrc: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      href: PropTypes.string.isRequired,
      isClosable: PropTypes.bool.isRequired,
      bgColor: PropTypes.string.isRequired,
    }),
  }),
};

export default PromobitApp;
